import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Mail, Lock, ArrowRight, Shield, Microscope, HeartPulse, CheckCircle2 } from 'lucide-react';
import { TextInput, Alert } from '../components/UI';
import { useAuth } from '../contexts/AuthContext';
import { API_URL, API_KEY } from '../config';

const FEATURES = [
  { icon: Microscope, label: 'AI Strip Analysis', desc: 'Automated 14-parameter urinalysis in under 2 seconds' },
  { icon: HeartPulse, label: 'Longitudinal Tracking', desc: 'Patient health trends across visits and time' },
  { icon: Shield, label: 'Wellness-Grade Security', desc: 'Encrypted, compliant data handling at every layer' },
];

const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@600;700&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  .cx-root {
    min-height: 100vh;
    display: flex;
    font-family: 'DM Sans', system-ui, sans-serif;
    background: #e8eef5;
  }

  /* ══ LEFT — mellow slate-blue ══ */
  .cx-left {
    flex: 0 0 44%;
    position: relative;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    padding: 56px 52px 52px;
    background: #dce6f0;
  }

  .cx-left-bg {
    position: absolute; inset: 0;
    background:
      radial-gradient(ellipse 75% 55% at 0% 0%,   rgba(0,130,200,0.10) 0%, transparent 55%),
      radial-gradient(ellipse 55% 60% at 100% 100%, rgba(80,130,190,0.08) 0%, transparent 55%);
    pointer-events: none;
  }

  .cx-left-dots {
    position: absolute; inset: 0;
    background-image: radial-gradient(circle, rgba(0,80,140,0.07) 1px, transparent 1px);
    background-size: 28px 28px;
    pointer-events: none;
  }

  .cx-inner {
    position: relative; z-index: 2;
    display: flex; flex-direction: column;
    height: 100%; justify-content: space-between;
  }

  /* Logo — clean bare image */
  .cx-logo-img {
    width: 240px;
    height: 40px;
    object-fit: contain;
    display: block;
    margin-bottom: 6px;
    filter: drop-shadow(0 3px 10px rgba(0,100,180,0.18));
  }

  .cx-brand-name {
    font-family: 'Cormorant Garamond', Georgia, serif;
    font-size: 40px;
    font-weight: 700;
    color: #0d2a42;
    line-height: 1.05;
    letter-spacing: -0.4px;
    margin-bottom: 5px;
  }

  .cx-brand-sub {
    font-size: 11.5px;
    color: #6a8aaa;
    font-weight: 400;
    letter-spacing: 2px;
    text-transform: uppercase;
    margin-bottom: 14px;
  }

  .cx-tagline {
    font-size: 13.5px;
    color: #5a7a96;
    line-height: 1.82;
    max-width: 278px;
    font-weight: 300;
  }

  .cx-rule {
    width: 36px; height: 2px;
    background: linear-gradient(90deg, rgba(0,130,200,0.45), transparent);
    border-radius: 2px;
    margin: 28px 0;
  }

  /* Features */
  .cx-feats { display: flex; flex-direction: column; gap: 20px; }

  .cx-feat { display: flex; align-items: flex-start; gap: 14px; }

  .cx-feat-ico {
    width: 34px; height: 34px;
    border-radius: 9px;
    background: rgba(0,120,190,0.1);
    border: 1px solid rgba(0,120,190,0.18);
    display: flex; align-items: center; justify-content: center;
    flex-shrink: 0; margin-top: 1px;
  }

  .cx-feat-name {
    font-size: 13px; font-weight: 600;
    color: #0d2a42; margin-bottom: 3px;
  }

  .cx-feat-desc {
    font-size: 12px; color: #7090aa;
    line-height: 1.65; font-weight: 300;
  }

  /* Disclaimer */
  .cx-disc {
    padding: 13px 16px;
    background: rgba(255,255,255,0.45);
    border: 1px solid rgba(0,100,160,0.1);
    border-radius: 10px;
  }

  .cx-disc p { font-size: 11.5px; color: #7090aa; line-height: 1.78; font-weight: 300; }
  .cx-disc strong { color: #3d6080; font-weight: 500; }

  /* ══ RIGHT — soft warm white ══ */
  .cx-right {
    flex: 0 0 56%;
    display: flex; align-items: center; justify-content: center;
    padding: 48px 72px;
    background: #f0f4f8;
    position: relative;
  }

  .cx-right::after {
    content: '';
    position: absolute; top: 0; right: 0;
    width: 360px; height: 360px;
    background: radial-gradient(circle at 100% 0%, rgba(0,130,200,0.05) 0%, transparent 60%);
    pointer-events: none;
  }

  .cx-form-wrap {
    width: 100%; max-width: 390px;
    position: relative; z-index: 1;
  }

  .cx-chip {
    display: inline-flex; align-items: center;
    padding: 4px 12px;
    background: rgba(0,110,170,0.08);
    border: 1px solid rgba(0,110,170,0.16);
    border-radius: 100px;
    font-size: 10px; font-weight: 600;
    letter-spacing: 1.6px; text-transform: uppercase;
    color: #006090; margin-bottom: 16px;
  }

  .cx-form-title {
    font-family: 'Cormorant Garamond', Georgia, serif;
    font-size: 34px; font-weight: 700;
    color: #0d2a42; letter-spacing: -0.3px;
    line-height: 1.13; margin-bottom: 8px;
  }

  .cx-form-sub {
    font-size: 14px; color: #7a96b0;
    line-height: 1.65; font-weight: 300; margin-bottom: 28px;
  }

  .cx-fields { display: flex; flex-direction: column; gap: 16px; }

  .cx-forgot-row { text-align: right; margin-top: 10px; margin-bottom: 22px; }
  .cx-forgot-row a { font-size: 13px; color: #006090; font-weight: 500; text-decoration: none; }
  .cx-forgot-row a:hover { text-decoration: underline; }

  .cx-cta {
    width: 100%; height: 50px; border-radius: 12px;
    background: linear-gradient(128deg, #005a8e 0%, #0090c0 100%);
    border: none; color: #fff;
    font-size: 15px; font-weight: 600;
    font-family: 'DM Sans', system-ui, sans-serif;
    cursor: pointer;
    display: flex; align-items: center; justify-content: center; gap: 8px;
    box-shadow: 0 4px 18px rgba(0,90,142,0.24);
    transition: transform 0.18s ease, box-shadow 0.18s ease;
    position: relative; overflow: hidden;
  }

  .cx-cta::before {
    content: '';
    position: absolute; top: 0; left: 0; right: 0; height: 50%;
    background: linear-gradient(180deg, rgba(255,255,255,0.12) 0%, transparent 100%);
    pointer-events: none;
  }

  .cx-cta:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 8px 26px rgba(0,90,142,0.32); }
  .cx-cta:active:not(:disabled) { transform: translateY(0); }
  .cx-cta:disabled { opacity: 0.6; cursor: not-allowed; }

  .cx-or { display: flex; align-items: center; gap: 14px; margin: 22px 0; }
  .cx-or-line { flex: 1; height: 1px; background: #cdd8e4; }
  .cx-or-txt  { font-size: 12px; color: #9ab0c4; white-space: nowrap; }

  .cx-ghost {
    width: 100%; height: 50px; border-radius: 12px;
    border: 1.5px solid #c4d2e0; background: #fff;
    color: #0d2a42; font-size: 14.5px; font-weight: 500;
    font-family: 'DM Sans', system-ui, sans-serif;
    cursor: pointer;
    display: flex; align-items: center; justify-content: center; gap: 8px;
    transition: background 0.18s, border-color 0.18s;
  }

  .cx-ghost:hover { background: #e6f0f8; border-color: rgba(0,130,190,0.3); }

  .cx-trust {
    display: flex; align-items: center; justify-content: center;
    gap: 20px; margin-top: 22px; padding-top: 18px;
    border-top: 1px solid #cdd8e4;
  }

  .cx-badge { display: flex; align-items: center; gap: 5px; font-size: 10.5px; color: #96aec4; }

  .cx-footer-note {
    text-align: center; margin-top: 18px;
    font-size: 11.5px; color: #a0b4c8; line-height: 1.7;
  }

  /* Animations */
  @keyframes cx-upIn   { from { opacity:0; transform:translateY(16px); } to { opacity:1; transform:translateY(0); } }
  @keyframes cx-leftIn { from { opacity:0; transform:translateX(-14px); } to { opacity:1; transform:translateX(0); } }

  .a-u0 { animation: cx-upIn   0.6s cubic-bezier(0.16,1,0.3,1) 0.04s both; }
  .a-u1 { animation: cx-upIn   0.6s cubic-bezier(0.16,1,0.3,1) 0.12s both; }
  .a-u2 { animation: cx-upIn   0.6s cubic-bezier(0.16,1,0.3,1) 0.20s both; }
  .a-u3 { animation: cx-upIn   0.6s cubic-bezier(0.16,1,0.3,1) 0.28s both; }
  .a-u4 { animation: cx-upIn   0.6s cubic-bezier(0.16,1,0.3,1) 0.36s both; }
  .a-l0 { animation: cx-leftIn 0.55s cubic-bezier(0.16,1,0.3,1) 0.18s both; }
  .a-l1 { animation: cx-leftIn 0.55s cubic-bezier(0.16,1,0.3,1) 0.28s both; }
  .a-l2 { animation: cx-leftIn 0.55s cubic-bezier(0.16,1,0.3,1) 0.38s both; }

  @media (max-width: 767px) {
    .cx-root { flex-direction: column; }
    .cx-left { flex: none; width: 100%; padding: 28px 22px 24px; }
    .cx-logo-img { width: 180px; height: 30px; margin-bottom: 6px; }
    .cx-brand-name { font-size: 28px; }
    .cx-tagline, .cx-rule, .cx-feats, .cx-disc { display: none; }
    .cx-right { flex: none; width: 100%; padding: 28px 20px 48px; }
    .cx-form-title { font-size: 26px; }
  }

  @media (min-width: 768px) and (max-width: 1099px) {
    .cx-left  { flex: 0 0 40%; padding: 44px 38px; }
    .cx-right { flex: 0 0 60%; padding: 40px 52px; }
    .cx-brand-name { font-size: 34px; }
    .cx-feat-desc { display: none; }
  }
`;

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [form, setForm] = useState({ username: '', password: '' });
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState(location.state?.message || '');
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!form.username || !form.password) { setError('Please enter your username and password.'); return; }
    setLoading(true); setError('');
    try {
      const res = await fetch(`${API_URL}/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Cytometrix-API-KEY': API_KEY },
        body: JSON.stringify({ username: form.username.toLowerCase(), password: form.password }),
      });
      const data = await res.json();
      if (res.ok) { login(form.username.toLowerCase(), data.email); navigate('/'); }
      else setError(data.message || 'Invalid username or password.');
    } catch { setError('Cannot connect to server. Please check your connection.'); }
    finally { setLoading(false); }
  };

  return (
    <>
      <style>{CSS}</style>
      <div className="cx-root">

        {/* ═══ LEFT ═══ */}
        <div className="cx-left">
          <div className="cx-left-bg" />
          <div className="cx-left-dots" />

          <div className="cx-inner">
            <div>
              <div className="a-u0">
                <img src="/logo.png" alt="Cytometrix" className="cx-logo-img" />
                <h1 className="cx-brand-name">Cytometrix</h1>
                <p className="cx-brand-sub">Urinalysis Platform</p>
                <p className="cx-tagline">
                  Advanced AI-powered wellness diagnostics built for modern clinical environments.
                </p>
              </div>

              <div className="cx-rule a-u1" />

              <div className="cx-feats">
                {FEATURES.map(({ icon: Icon, label, desc }, i) => (
                  <div key={label} className={`cx-feat a-l${i}`}>
                    <div className="cx-feat-ico">
                      <Icon size={15} color="#0080b8" strokeWidth={2} />
                    </div>
                    <div>
                      <p className="cx-feat-name">{label}</p>
                      <p className="cx-feat-desc">{desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="cx-disc a-u2">
              <p>
                <strong>For wellness purposes only.</strong> Cytometrix is not a medical device and does not provide clinical diagnoses or medical advice. Always consult a qualified healthcare professional.
              </p>
            </div>
          </div>
        </div>

        {/* ═══ RIGHT ═══ */}
        <div className="cx-right">
          <div className="cx-form-wrap">

            <div className="a-u0">
              <div className="cx-chip">Clinician Login</div>
              <h2 className="cx-form-title">Welcome back</h2>
              <p className="cx-form-sub">Sign in to access your Cytometrix wellness dashboard.</p>
            </div>

            {successMsg && <div style={{ marginBottom: 18 }} className="a-u1"><Alert type="success">{successMsg}</Alert></div>}
            {error      && <div style={{ marginBottom: 18 }} className="a-u1"><Alert type="error">{error}</Alert></div>}

            <form onSubmit={handleLogin}>
              <div className="cx-fields a-u1">
                <TextInput
                  label="Username" icon={Mail} required
                  value={form.username} onChange={e => set('username', e.target.value)}
                  placeholder="Enter your username" autoComplete="username"
                />
                <TextInput
                  label="Password" icon={Lock} type="password" required
                  value={form.password} onChange={e => set('password', e.target.value)}
                  placeholder="Enter your password" autoComplete="current-password"
                />
              </div>

              <div className="cx-forgot-row a-u2">
                <Link to="/forgot-password">Forgot password?</Link>
              </div>

              <div className="a-u3">
                <button type="submit" className="cx-cta" disabled={loading}>
                  {loading
                    ? 'Signing in…'
                    : <><span>Sign In</span><ArrowRight size={16} strokeWidth={2.5} /></>
                  }
                </button>
              </div>
            </form>

            <div className="cx-or a-u3">
              <div className="cx-or-line" />
              <span className="cx-or-txt">New to Cytometrix?</span>
              <div className="cx-or-line" />
            </div>

            <div className="a-u4">
              <button className="cx-ghost" onClick={() => navigate('/signup')}>
                Create an Account
              </button>
            </div>

            <div className="cx-trust a-u4">
              {[['Encrypted', Shield], ['HIPAA Aligned', CheckCircle2], ['Wellness Only', HeartPulse]].map(([lbl, Icon]) => (
                <div key={lbl} className="cx-badge">
                  <Icon size={12} color="#96aec4" />
                  <span>{lbl}</span>
                </div>
              ))}
            </div>

            <p className="cx-footer-note">
              For authorized wellness professionals only.<br />
              Unauthorized access is strictly prohibited.
            </p>
          </div>
        </div>

      </div>
    </>
  );
}
